import { useEffect, useState } from "react";
import { classNames, formatJSON } from "../lib/format";

type Props = {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  /** Quando true, exibe a contagem de bytes e o status do parse. */
  showFooter?: boolean;
  className?: string;
};

// Editor de JSON simples (textarea estilizado). Faz parse "preview"
// para sinalizar erro de sintaxe sem bloquear o envio — o usuário pode
// querer mandar JSON inválido para ver o erro 400 da API.
export function JSONEditor({
  value,
  onChange,
  placeholder,
  rows = 18,
  showFooter = true,
  className,
}: Props) {
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    if (!value.trim()) {
      setParseError(null);
      return;
    }
    try {
      JSON.parse(value);
      setParseError(null);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err));
    }
  }, [value]);

  const bytes = new TextEncoder().encode(value).byteLength;
  return (
    <div className={classNames("space-y-1", className)}>
      <textarea
        className={classNames(
          "ks-input ks-code ks-scroll resize-y",
          "min-h-[160px] whitespace-pre",
        )}
        rows={rows}
        spellCheck={false}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {showFooter && (
        <div className="flex items-center justify-between text-[11px] text-ink-500">
          <span>
            {bytes.toLocaleString("pt-BR")} bytes · {value.split("\n").length} linhas
          </span>
          {parseError ? (
            <span className="font-medium text-rose-600">JSON inválido: {parseError}</span>
          ) : value.trim() ? (
            <span className="text-brand-700">JSON válido</span>
          ) : (
            <span>aguardando entrada</span>
          )}
        </div>
      )}
    </div>
  );
}

// Helper para componentes que precisam serializar a partir de um objeto
// e descartar o "" inicial.
export function toJSONString(value: unknown): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") {
    // Se já for string, tentamos re-formatar para indentar.
    try {
      return formatJSON(JSON.parse(value));
    } catch {
      return value;
    }
  }
  return formatJSON(value);
}
